import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  GetListPermissionsByTypeResponse,
  GetListAgencyUsersParams,
  GetListAgencyUsersResponse,
  UpdateAgencyPortallAccessParams,
  GetListAgencyContactsParams,
  GetListAgencyContactsResponse,
  UpdateAgencyPermissionsParams,
  UpdateAgencyContactPermissionsParams,
  GetListCandidatesParams,
  GetListCandidatesResponse,
  UpdateCandidatePortalAccessParams,
  UpdateCandidatePermissionsParams,
  SetAllCandidatesOfAgencyPermissionsParams,
  GetListCompanyUsersResponse,
  GetListCompanyUsersParams,
  GetListCompanyUserGroupsParams,
  GetListCompanyUserGroupsResponse,
  UpdateCompanyUserPortalAccessParams,
  UpdateCompanyUserPortalAccessResponse,
  UpdateCompanyUserParams,
  CreateCompanyUserParams,
  DeleteUserGroupParams,
  CreateUserGroupParams,
  UpdateUserGroupParams,
  GetListEmployeesPermissionsParams,
  GetListEmployeesPermissionsResponse,
  UpdateEmployeePortalAccessParams,
  UpdateAllEmployeesOfEmployerPermissionsParams,
  CreateBureauCompanyUserParams,
  UpdateBureauCompanyUserParams,
} from '../types';
import { CompanyUserGroupRecordType } from '@nxsys/types/models/permissions/company';
import { APP_NAME_BUREAU } from '@nxsys/store/constants/keys';
import { PaginationParamsTypeWithSearch } from '@nxsys/types/models/Common';

const getListAgencyPermissions = createAsyncThunk(
  `/getListAgencyPermissions`,
  async (_, { rejectWithValue }): Promise<GetListPermissionsByTypeResponse> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.permissions}`,
        {
          params: {
            type: 'AGENCY',
          },
        },
      );
      return {
        data: response.data.data,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListCompanyPermissions = createAsyncThunk(
  `/getListCompanyPermissions`,
  async (_, { rejectWithValue }): Promise<GetListPermissionsByTypeResponse> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.permissions}`,
        {
          params: {
            type: 'COMPANY',
          },
        },
      );
      return {
        data: response.data.data,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListAgencyUsers = createAsyncThunk(
  `/getListAgencyUsers`,
  async (
    params: GetListAgencyUsersParams,
    { rejectWithValue },
  ): Promise<GetListAgencyUsersResponse & { queryParams: GetListAgencyUsersParams | null }> => {
    try {
      const response = await jwtAxios.get(`${endPoints.agency.main}`, {
        params,
      });

      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateAgencyPortallAccess = createAsyncThunk(
  `/updateAgencyPortallAccess`,
  async (params: UpdateAgencyPortallAccessParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.agency.main}/${params.agencyId}`,
        {
          allowPortalAccess: params.allowPortalAccess,
        },
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateAgencyPermissionsAll = createAsyncThunk(
  `/updateAgencyPermissionsAll`,
  async (params: string[], { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.updateAgencyAll}`,
        params,
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateAgencyPermissions = createAsyncThunk(
  `/updateAgencyPermissions`,
  async (params: UpdateAgencyPermissionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.agency.main}/${params.agencyId}`,
        params,
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListAgencyContacts = createAsyncThunk(
  `/getListAgencyContacts`,
  async (
    params: GetListAgencyContactsParams,
    { rejectWithValue },
  ): Promise<GetListAgencyContactsResponse> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.agency.contact}/${params.agencyId}`,
        {
          params,
        },
      );

      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateAgencyContactPermissions = createAsyncThunk(
  `/updateAgencyContactPermissions`,
  async (params: UpdateAgencyContactPermissionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.agency.contact}/${params.agencyContactId}`,
        params,
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListCandidates = createAsyncThunk(
  `/getListCandidates`,
  async (
    params: GetListCandidatesParams,
    { rejectWithValue },
  ): Promise<GetListCandidatesResponse & { queryParams: GetListCandidatesParams | null }> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.candidates}`,
        {
          params: {
            ...params,
            employerType: 'CANDIDATE',
          },
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateCandidatePortalAccess = createAsyncThunk(
  `/updateCandidatePortalAccess`,
  async (params: UpdateCandidatePortalAccessParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.employee.get}/${params.candidateId}`,
        {
          allowPortalLogin: params.allowPortalLogin,
        },
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListCandidatePermissions = createAsyncThunk(
  `/getListCandidatePermissions`,
  async (_, { rejectWithValue }): Promise<GetListPermissionsByTypeResponse> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.permissions}`,
        {
          params: {
            type: 'EMPLOYEE',
          },
        },
      );
      return {
        data: response.data.data,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateCandidatePermissions = createAsyncThunk(
  `/updateCandidatePermissions`,
  async (params: UpdateCandidatePermissionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.permissions.updateCandidate}`,
        params,
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const setAllCandidatesOfAgencyPermissions = createAsyncThunk(
  `/setAllCandidatesOfAgencyPermissions`,
  async (
    params: SetAllCandidatesOfAgencyPermissionsParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.permissions.updateCandidateAll}?agencyId=${params.agencyId}`,
        params.permissions,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListCompanyUsers = createAsyncThunk(
  `/getListCompanyUsers`,
  async (
    params: GetListCompanyUsersParams,
    { rejectWithValue },
  ): Promise<GetListCompanyUsersResponse & { queryParams: GetListCompanyUsersParams | null }> => {
    try {
      const response = await jwtAxios.get(`${endPoints.user.admin}`, {
        params,
      });

      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListCompanyUserGroups = createAsyncThunk(
  `/getListCompanyUserGroups`,
  async (
    params: GetListCompanyUserGroupsParams,
    { rejectWithValue },
  ): Promise<GetListCompanyUserGroupsResponse & { queryParams: GetListCompanyUserGroupsParams | null }> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.userGroups}`,
        {
          params,
        },
      );

      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateCompanyUserPortalAccess = createAsyncThunk(
  `/updateCompanyUserPortalAccess`,
  async (
    params: UpdateCompanyUserPortalAccessParams,
    { rejectWithValue },
  ): Promise<UpdateCompanyUserPortalAccessResponse> => {
    const { userId, isBlocked } = params;
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.editUserCompany}/${userId}`,
        {
          isBlocked,
        },
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateCompanyUser = createAsyncThunk(
  `/updateCompanyUser`,
  async (params: UpdateCompanyUserParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.editUserCompany}/${params.userId}`,
        params,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const createCompanyUser = createAsyncThunk(
  `/createCompanyUser`,
  async (params: CreateCompanyUserParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.permissions.createUserCompany}`,
        params,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const deleteUserGroup = createAsyncThunk(
  `/deleteUserGroup`,
  async (params: DeleteUserGroupParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.permissions.userGroups}/${params.groupId}`,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const createUserGroup = createAsyncThunk(
  `/createUserGroup`,
  async (
    params: CreateUserGroupParams,
    { rejectWithValue },
  ): Promise<CompanyUserGroupRecordType> => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.permissions.userGroups}`,
        params,
      );
      return response.data.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateUserGroup = createAsyncThunk(
  `/updateUserGroup`,
  async (params: UpdateUserGroupParams, { rejectWithValue }) => {
    const { groupId, name, desc, permissions, departments } = params;
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.userGroups}/${groupId}`,
        {
          name,
          desc,
          permissions,
          departments,
        },
      );
      return response.data.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getListEmployeesPermissions = createAsyncThunk(
  `/getListEmployeesPermissions`,
  async (
    params: GetListEmployeesPermissionsParams,
    { rejectWithValue },
  ): Promise<GetListEmployeesPermissionsResponse & { queryParams: GetListEmployeesPermissionsParams | null }> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.candidates}`,
        {
          params: {
            ...params,
            appName: APP_NAME_BUREAU,
          },
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateEmployeePermissions = createAsyncThunk(
  `/updateEmployeePermissions`,
  async (params: UpdateCandidatePermissionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.permissions.updateCandidate}`,
        params,
      );

      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateEmployeePortalAccess = createAsyncThunk(
  `/updateEmployeePortalAccess`,
  async (params: UpdateEmployeePortalAccessParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.employee.get}/${params.employeeId}`,
        {
          allowPortalLogin: params.allowPortalLogin,
        },
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateAllEmployeesOfEmployerPermissions = createAsyncThunk(
  `/updateAllEmployeesOfEmployerPermissions`,
  async (
    params: UpdateAllEmployeesOfEmployerPermissionsParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.patch(
        `${endPoints.permissions.updateCandidateCompany}?companyId=${params.companyId}`,
        params.permissions,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const getBureauCompanyUsers = createAsyncThunk(
  `/getBureauCompanyUsers`,
  async (params: PaginationParamsTypeWithSearch, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.permissions.usersCompany}`,
        { params },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateBureauCompanyUserPortalAccess = createAsyncThunk(
  `/updateBureauCompanyUserPortalAccess`,
  async (params: UpdateCompanyUserPortalAccessParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.editUserCompany}/${params.userId}`,
        params,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const createBureauCompanyUser = createAsyncThunk(
  `/createBureauCompanyUser`,
  async (params: CreateBureauCompanyUserParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.permissions.createUserCompany}`,
        params,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

const updateBureauCompanyUser = createAsyncThunk(
  `/updateBureauCompanyUser`,
  async (params: UpdateBureauCompanyUserParams, { rejectWithValue }) => {
    const { userId, ...rest } = params;
    try {
      const response = await jwtAxios.put(
        `${endPoints.permissions.editUserCompany}/${userId}`,
        rest,
      );
      return response.data;
    } catch (e) {
      throw rejectWithValue(e as Error);
    }
  },
);

export const permissionsAct = {
  getListAgencyPermissions,
  getListCompanyPermissions,
  getListAgencyUsers,
  updateAgencyPortallAccess,
  updateAgencyPermissionsAll,
  getListAgencyContacts,
  updateAgencyPermissions,
  updateAgencyContactPermissions,
  getListCandidates,
  updateCandidatePortalAccess,
  getListCandidatePermissions,
  updateCandidatePermissions,
  setAllCandidatesOfAgencyPermissions,
  getListCompanyUsers,
  getListCompanyUserGroups,
  updateCompanyUserPortalAccess,
  updateCompanyUser,
  createCompanyUser,
  deleteUserGroup,
  createUserGroup,
  updateUserGroup,
  getListEmployeesPermissions,
  updateEmployeePermissions,
  updateEmployeePortalAccess,
  updateAllEmployeesOfEmployerPermissions,
  getBureauCompanyUsers,
  updateBureauCompanyUserPortalAccess,
  createBureauCompanyUser,
  updateBureauCompanyUser,
};
